import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search } from "lucide-react";
import { toast } from "sonner";

const ROLES = [{ value: "directeur", label: "Directeur" }, { value: "maitre", label: "Enseignant" }, { value: "admin", label: "Administrateur" }];

export default function UserAccessDialog({ target, onClose, onSaved }) {
  const [role, setRole] = useState("");
  const [schools, setSchools] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!target) return;
    setRole(target.role);
    setSelected([...(new Set([...(target.school_ids || []), ...(target.school_id ? [target.school_id] : [])]))]);
    api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
  }, [target]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    const body = { role };
    if (role === "directeur") {
      body.school_ids = selected;
      body.school_id = selected.includes(target.school_id) ? target.school_id : selected[0] || null;
    }
    try { await api.patch(`/users/${target.id}`, body); toast.success("Accès mis à jour"); onSaved(); onClose(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const shown = schools.filter((s) => !q || (s.nom || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-access-dialog">
        <DialogHeader><DialogTitle>Rôle et écoles — {target?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Rôle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="access-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {role === "directeur" && (
            <div>
              <Label>Écoles dirigées ({selected.length})</Label>
              <div className="relative mt-1.5">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
                <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une école…" />
              </div>
              <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-[#E2E8F0] divide-y divide-[#E2E8F0]">
                {shown.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-[#F6F8F9]" data-testid="access-school-option">
                    <input type="checkbox" className="w-4 h-4 accent-[#1F7A63]" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
                    <span className="flex-1">{s.nom}</span>
                    <span className="text-xs text-[#5C736A]">{s.ief || ""}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#5C736A] mt-1.5">Le directeur pourra basculer d&apos;une école à l&apos;autre et voir les statistiques globales.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="access-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
